import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Product, ProductCategory } from "@/types/product";
import {
  Package,
  Tag,
  FileText,
  List,
  Plus,
  Edit,
  Trash2,
  Hash,
} from "lucide-react";
import { CreateRuleModal } from "./CreateRuleModal";
import { useProducts } from "./ProductsContext";
import { useAuth } from "@/hooks/useAuth";

interface ProductDetailProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  categories: ProductCategory[];
}

export function ProductDetail({
  product: initialProduct,
  isOpen,
  onClose,
  categories,
}: ProductDetailProps) {
  const { createRule, updateRule, deleteRule, products } = useProducts();
  const { company, can } = useAuth();
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);

  if (!initialProduct) return null;

  const product =
    products.find((p) => p.id === initialProduct.id) || initialProduct;

  const category = categories.find((c) => c.id === product.categoryId);

  const getCurrencySymbol = () => {
    if ((company as any)?.currency) return (company as any).currency;

    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {
      console.error("Error reading currency from storage", e);
    }
    return "$";
  };

  const currencySymbol = getCurrencySymbol();

  const formatPriceModifier = (modifier: string | number, type: string) => {
    const numModifier =
      typeof modifier === "string" ? parseFloat(modifier) : modifier;
    const sign = numModifier >= 0 ? "+" : "";

    if (type === "percentage") {
      return `${sign}${numModifier}%`;
    }
    return `${sign}${currencySymbol}${numModifier.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getPriceTypeLabel = (type: string) => {
    switch (type) {
      case "percentage":
        return "Porcentaje";
      case "per_unit":
        return "Por unidad";
      case "fixed":
        return "Fijo";
      default:
        return type;
    }
  };

  const handleCreateRule = async (ruleData: any) => {
    if (editingRule) {
      await updateRule(editingRule.id, ruleData);
    } else {
      await createRule({
        ...ruleData,
        product_id: product.id,
      });
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar esta regla?")) {
      await deleteRule(ruleId, product.id);
    }
  };

  const calculateTotalWithRules = () => {
    if (!product.rules || product.rules.length === 0) return product.unitPrice;

    let total = product.unitPrice;
    product.rules.forEach((rule) => {
      const modifier =
        typeof rule.price_modifier === "string"
          ? parseFloat(rule.price_modifier)
          : rule.price_modifier;

      if (rule.price_type === "percentage") {
        total += (total * modifier) / 100;
      } else {
        total += modifier;
      }
    });
    return total;
  };

  const totalWithRules = calculateTotalWithRules();
  const hasRules = product.rules && product.rules.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto p-4">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center">
              <Package className="h-5 w-5 mr-2 text-blue-600" />
              <div>
                <DialogTitle className="text-lg font-semibold">
                  {product.name}
                </DialogTitle>
                <div className="flex items-center mt-0.5">
                  <Hash className="h-3 w-3 mr-1 text-slate-400" />
                  <span className="text-xs text-slate-600 font-mono">
                    {product.sku}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card>
              <CardContent className="p-3 space-y-2">
                <h3 className="font-semibold text-base mb-2">
                  Información del Producto
                </h3>

                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">
                    Descripción
                  </label>
                  <p className="text-sm text-slate-900 whitespace-pre-wrap bg-slate-50 p-2 rounded-md">
                    {product.description || "Sin descripción"}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600 flex items-center mb-1">
                      <Tag className="h-3 w-3 mr-1" />
                      Categoría
                    </label>
                    {category ? (
                      <Badge
                        style={{
                          backgroundColor: category.color + "20",
                          color: category.color,
                        }}
                        className="text-xs px-2 py-0.5"
                      >
                        {category.name}
                      </Badge>
                    ) : (
                      <p className="text-slate-500 text-xs">Sin categoría</p>
                    )}
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">
                      Unidad
                    </label>
                    <p className="text-sm text-slate-900 font-medium">
                      {product.unit}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <Card>
                <CardContent className="p-3">
                  <h3 className="font-semibold text-base mb-2 flex items-center">
                    <Tag className="h-4 w-4 mr-1 text-green-600" />
                    Precio
                  </h3>
                  <div className="space-y-2">
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-lg border border-green-100">
                      <p className="text-xs font-medium text-green-700 mb-0.5">
                        Precio Base
                      </p>
                      <p className="text-2xl font-bold text-green-700">
                        {currencySymbol}
                        {product.unitPrice.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        Por {product.unit.toLowerCase()}
                      </p>
                    </div>

                    {hasRules && (
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-100">
                        <p className="text-xs font-medium text-blue-700 mb-0.5">
                          Precio con Ajustes
                        </p>
                        <p className="text-2xl font-bold text-blue-700">
                          {currencySymbol}
                          {totalWithRules.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          Aplicando {product.rules.length} ajuste(s)
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {product.attachments && product.attachments.length > 0 && (
                <Card>
                  <CardContent className="p-3">
                    <h3 className="font-semibold text-base mb-2 flex items-center">
                      <FileText className="h-4 w-4 mr-1 text-blue-600" />
                      Archivos Adjuntos ({product.attachments.length})
                    </h3>
                    <div className="space-y-2">
                      {product.attachments.map((att, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-blue-50 rounded-md border border-blue-100 hover:border-blue-200 transition-colors"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-xs text-blue-900">
                              {att.nombre}
                            </p>
                            {att.descripcion && (
                              <p className="text-[10px] text-blue-700 mt-0.5">
                                {att.descripcion}
                              </p>
                            )}
                          </div>
                          {att.tamano && (
                            <span className="text-[10px] font-medium text-blue-600 bg-white px-1.5 py-0.5 rounded">
                              {(att.tamano / 1024).toFixed(1)} KB
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-base flex items-center">
                  <List className="h-4 w-4 mr-1 text-slate-600" />
                  Ajustes de Precio ({product.rules?.length || 0})
                </h3>
                {can("products.update") && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingRule(null);
                      setIsRuleModalOpen(true);
                    }}
                    className="h-7 px-3 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Agregar Ajuste
                  </Button>
                )}
              </div>

              {product.rules && product.rules.length > 0 ? (
                <div className="space-y-2">
                  {product.rules.map((rule) => (
                    <div
                      key={rule.id}
                      className="p-2 bg-white rounded-md border border-slate-200 hover:border-blue-300 transition-colors shadow-sm"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-sm">
                              {rule.rule_name}
                            </p>
                            {rule.is_required && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] px-1.5 py-0"
                              >
                                Obligatorio
                              </Badge>
                            )}
                          </div>
                          {rule.description && (
                            <p className="text-xs text-slate-600">
                              {rule.description}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-3">
                          <span
                            className={`font-bold text-sm ${(typeof rule.price_modifier === "string"
                              ? parseFloat(rule.price_modifier)
                              : rule.price_modifier) >= 0
                              ? "text-green-600"
                              : "text-red-600"
                              }`}
                          >
                            {formatPriceModifier(
                              rule.price_modifier,
                              rule.price_type,
                            )}
                          </span>
                          <p className="text-[10px] text-slate-500">
                            {getPriceTypeLabel(rule.price_type)}
                          </p>
                          <div className="flex justify-end gap-1 mt-1">
                            {can("products.update") && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => {
                                  setEditingRule(rule);
                                  setIsRuleModalOpen(true);
                                }}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            )}
                            {can("products.update") && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => handleDeleteRule(rule.id)}
                                title="Eliminar ajuste"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 bg-slate-50 rounded-md border border-dashed border-slate-300">
                  <List className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-slate-600 font-medium text-sm">
                    No hay ajustes configurados
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Agrega ajustes para modificar el precio base del producto
                  </p>
                  {can("products.update") && (
                    <Button
                      variant="outline"
                      className="mt-2 h-7 text-xs"
                      onClick={() => {
                        setEditingRule(null);
                        setIsRuleModalOpen(true);
                      }}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Crear primer ajuste
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>

      <CreateRuleModal
        isOpen={isRuleModalOpen}
        onClose={() => {
          setIsRuleModalOpen(false);
          setEditingRule(null);
        }}
        onSave={handleCreateRule}
        editingRule={editingRule}
      />
    </Dialog>
  );
}
